import React from 'react';
import { AfterSaleRecord, Detail } from '../types';
import { aftersaleHumanizeDate } from 'pages/newaftersale/detail/utils';
import { CardInfo } from '../components/aftersale-card';
import { Address } from '../components/aftersale-render';

// 售后操作
export const AFTERSALE_ACTION_TYPES = {
    CANCEL_REFUND: 'CANCEL_REFUND', // 取消退款
    CANCEL_RETURN: 'CANCEL_RETURN', // 取消退货
    ADD_LOGISTICS: 'ADD_LOGISTICS', // 填写物流
    LOGISTICS: 'LOGISTICS', // 查看物流
    APPLY_ARBITRATE: 'APPLY_ARBITRATE', // 客服申诉
    CONFIRM: 'CONFIRM', // 确认收货
    MODIFY_AFTER: 'MODIFY_AFTER', // 修改售后信息
    MODIFY_PAYEE: 'MODIFY_PAYEE', // 修改收款信息
    ADD_PAYEE: 'ADD_PAYEE', // 填写收款信息
    CUSTOMER_SERVICE: 'CUSTOMER_SERVICE', // 联系客服
    CANCEL_EXCHANGE: 'CANCEL_EXCHANGE', // 取消换货
    MODIFY_EXCHANGE: 'MODIFY_EXCHANGE' // 修改换货信息
};

// 售后操作名称
export const AFTERSALE_ACTION_NAMES = {
    [AFTERSALE_ACTION_TYPES.CANCEL_REFUND]: '取消退款',
    [AFTERSALE_ACTION_TYPES.CANCEL_RETURN]: '取消退货',
    [AFTERSALE_ACTION_TYPES.ADD_LOGISTICS]: '填写物流',
    [AFTERSALE_ACTION_TYPES.LOGISTICS]: '查看物流',
    [AFTERSALE_ACTION_TYPES.APPLY_ARBITRATE]: '客服申诉',
    [AFTERSALE_ACTION_TYPES.CONFIRM]: '确认收货',
    [AFTERSALE_ACTION_TYPES.MODIFY_AFTER]: '修改售后信息',
    [AFTERSALE_ACTION_TYPES.MODIFY_PAYEE]: '修改收款信息',
    [AFTERSALE_ACTION_TYPES.ADD_PAYEE]: '填写收款信息',
    [AFTERSALE_ACTION_TYPES.CUSTOMER_SERVICE]: '联系客服',
    [AFTERSALE_ACTION_TYPES.CANCEL_EXCHANGE]: '取消换货',
    [AFTERSALE_ACTION_TYPES.MODIFY_EXCHANGE]: '修改换货信息'
};

// 售后类型
export enum AFTERSALE_TYPE {
    Init = 0, // 售后退货退款
    Refund = 1, // 售后退款
    PresaleAll = 2, // 售前退款
    Exchange = 3 // 换货
}

export const AFTERSALE_TYPE_NAMES: {
    [index: number]: string;
} = {
    [AFTERSALE_TYPE.Init]: '退货退款',
    [AFTERSALE_TYPE.Refund]: '仅退款',
    [AFTERSALE_TYPE.PresaleAll]: '仅退款',
    [AFTERSALE_TYPE.Exchange]: '换货'
};

// 售后状态
export enum AFTERSALE_STATUS {
    Init = 0, // 售后初始化
    Apply = 6, // 售后申请
    Refuse = 27, // 拒绝售后申请
    Success = 12, // 售后成功
    Close = 28, // 售后关闭
    Return = 7, // 售后退货中
    Logistics = 11, // 售后已发货
    LogisticsRefuse = 29, // 售后退货拒绝
    HaveSend = 13, // 商家已发货
    UserReceive = 14 // 用户接收
}

// 订单退款状态
export enum ORDER_REFUND_STATUS {
    Init = 0, // 无需退款
    Waiting = 1, // 待退款
    Refunding = 2, // 退款中
    Success = 3, // 退款成功
    Fail = 4 // 退款失败
}

// 售后操作类型
export const AFTERSALE_OP_TYPE = {
    apply: 'apply', // 售后申请
    cancel_apply: 'cancel_apply', // 售后取消
    close: 'close', // 售后关闭
    audit: 'audit', // 售后审核
    apply_arbitrate: 'apply_arbitrate', // 申请仲裁
    arbitrate_audit: 'arbitrate_audit', // 仲裁处理
    logistics: 'logistics', // 填写退货物流
    logistics_audit: 'logistics_audit', // 退货物流审核
    apply_logistics_arbitrate: 'apply_logistics_arbitrate', // 申请退货物流仲裁
    logistics_arbitrate_audit: 'logistics_arbitrate_audit', // 申请退货物流处理
    aftersale_fail: 'aftersale_fail', // 售后失败
    refund: 'refund', // 退款
    refund_fail: 'refund_fail', // 退款失败
    logistics_exchange: 'logistics_exchange', // 商家换货发货
    success_exchange: 'success_exchange', // 换货成功
    delay_receive: 'delay_receive', // 延长收货
    exchange_arbitrate_audit: 'exchange_arbitrate_audit' // 客服同意换货
};

// 售后阶段类型
export const AFTERSALE_STAGE_TYPES = {
    apply: '提交申请',
    audit: '商家审核',
    logistics: '填写物流',
    refund: '退款完成',
    backGoods: '退回商品',
    resendGoods: '商家发货',
    exchange: '完成'
};

// 支付方式
export enum PAY_TYPES {
    COD = 0, // 货到付款
    WeChat = 1,
    AliPay = 2
}

// 退款（支付）路径
const PAY_RETURN_PATH: {
    [key in PAY_TYPES]: string;
} = {
    [PAY_TYPES.COD]: '退款',
    [PAY_TYPES.WeChat]: '微信',
    [PAY_TYPES.AliPay]: '支付宝'
};

// 售后阶段，根据售后类型展示不同的阶段 售后进度条
export const AFTERSALE_STAGES: {
    [key in AFTERSALE_TYPE]: typeof AFTERSALE_STAGE_TYPES[keyof typeof AFTERSALE_STAGE_TYPES][];
} = {
    [AFTERSALE_TYPE.Init]: [
        AFTERSALE_STAGE_TYPES.apply,
        AFTERSALE_STAGE_TYPES.audit,
        AFTERSALE_STAGE_TYPES.logistics,
        AFTERSALE_STAGE_TYPES.refund
    ],
    [AFTERSALE_TYPE.Refund]: [
        AFTERSALE_STAGE_TYPES.apply,
        AFTERSALE_STAGE_TYPES.audit,
        AFTERSALE_STAGE_TYPES.refund
    ],
    [AFTERSALE_TYPE.PresaleAll]: [
        AFTERSALE_STAGE_TYPES.apply,
        AFTERSALE_STAGE_TYPES.audit,
        AFTERSALE_STAGE_TYPES.refund
    ],
    [AFTERSALE_TYPE.Exchange]: [
        AFTERSALE_STAGE_TYPES.apply,
        AFTERSALE_STAGE_TYPES.audit,
        AFTERSALE_STAGE_TYPES.backGoods,
        AFTERSALE_STAGE_TYPES.resendGoods,
        AFTERSALE_STAGE_TYPES.exchange
    ]
};

// 物流状态
export const LOGISTICS_STATE = {
    isSign: '3'
};

/**
 * 所有售后状态
 * NOTES:
 * 该状态与 AFTERSALE_STATUS 不同，AFTERSALE_STATUS 只是一个笼统的售后状态
 * 而该状态特指具体的当前状态阶段
 */
export enum AFTERSALE_ALL_STATUS {
    // AFTERSALE_TYPE Init（售后退款退货）
    售后退货退款_申请售后 = '售后退货退款_申请售后',
    售后退货退款_取消申请 = '售后退货退款_取消申请',
    售后退货退款_商户审核_同意 = '售后退货退款_商户审核_同意',
    售后退货退款_商户审核_拒绝 = '售后退货退款_商户审核_拒绝',
    售后退货退款_用户申请仲裁 = '售后退货退款_用户申请仲裁',
    售后退货退款_客服仲裁_支持用户 = '售后退货退款_客服仲裁_支持用户',
    售后退货退款_客服仲裁_支持商家 = '售后退货退款_客服仲裁_支持商家',
    售后退货退款_填写物流 = '售后退货退款_填写物流',
    售后退货退款_货物审核_同意 = '售后退货退款_货物审核_同意',
    售后退货退款_货物审核_拒绝 = '售后退货退款_货物审核_拒绝',
    售后退货退款_货物审核_同意_退款失败 = '售后退货退款_货物审核_同意_退款失败',
    售后退货退款_退货仲裁 = '售后退货退款_退货仲裁',
    售后退货退款_退货仲裁_支持用户 = '售后退货退款_退货仲裁_支持用户',
    售后退货退款_退货仲裁_支持商家 = '售后退货退款_退货仲裁_支持商家',
    售后退货退款_退款成功 = '售后退货退款_退款成功',
    售后退货退款_退款失败 = '售后退货退款_退款失败',
    售后退货退款_打款失败 = '售后退货退款_打款失败',
    售后退货退款_商家审核_退款失败 = '售后退货退款_商家审核_退款失败',
    售后退货退款_退货申请 = '售后退货退款_退货申请',
    // AFTERSALE_TYPE Refund（售后退款）
    售后退款_取消申请 = '售后退款_取消申请',
    售后退款_申请售后 = '售后退款_申请售后',
    售后退款_商户审核_同意 = '售后退款_商户审核_同意',
    售后退款_商户审核_拒绝 = '售后退款_商户审核_拒绝',
    售后退款_用户申请仲裁 = '售后退款_用户申请仲裁',
    售后退款_客服仲裁_支持用户 = '售后退款_客服仲裁_支持用户',
    售后退款_客服仲裁_支持商家 = '售后退款_客服仲裁_支持商家',
    售后退款_客退款成功 = '售后退款_客退款成功',
    售后退货退款_商家审核_商家同意 = '售后退货退款_商家审核_商家同意',
    // AFTERSALE_TYPE PresaleAll（售前退款）
    售前退款_申请售后 = '售前退款_申请售后',
    售前退款_取消申请 = '售前退款_取消申请',
    售前退款_商户审核_同意 = '售前退款_商户审核_同意',
    售前退款_退款成功 = '售前退款_退款成功',
    // AFTERSALE_TYPE Exchange（换货）
    售后换货_申请售后 = '售后换货_申请售后',
    售后换货_商户审核_同意 = '售后换货_商户审核_同意',
    售后换货_商户审核_拒绝 = '售后换货_商户审核_拒绝',
    售后换货_用户申请仲裁 = '售后换货_用户申请仲裁',
    售后换货_客服仲裁_支持用户 = '售后换货_客服仲裁_支持用户',
    售后换货_客服仲裁_支持商家 = '售后换货_客服仲裁_支持商家',
    售后换货_已填写物流 = '售后换货_已填写物流',
    售后换货_商家已发货 = '售后换货_商家已发货',
    售后换货_已填写物流_商户拒绝 = '售后换货_已填写物流_商户拒绝',
    售后换货_已填写物流_用户申请仲裁 = '售后换货_已填写物流_用户申请仲裁',
    售后换货_已填写物流_客服支持用户 = '售后换货_已填写物流_客服支持用户',
    售后换货_二次仲裁_客服支持用户 = '售后换货_二次仲裁_客服支持用户',
    售后换货_已填写物流_客服支持商家 = '售后换货_已填写物流_客服支持商家',
    售后换货_已填写物流_延长收货 = '售后换货_已填写物流_延长收货',
    售后换货_换货成功 = '售后换货_换货成功',
    售后换货_用户接收 = '售后换货_用户接收',
    售后换货_换货_退款成功 = '售后换货_换货_退款成功',
    售后换货_换货失败 = '售后换货_换货失败',
    售后换货_换货取消 = '售后换货_换货取消',
    售后换货_客服同意退款 = '售后换货_客服同意退款'
}

/**
 * 售后阶段实体
 * NOTES: 用于判断当前所处的售后阶段
 *
 * 步骤:
 * 1. 在 selector.getAftersaleStageInfo 方法中会根据当前售后类型，找到该类型下所有可能的阶段
 * 2. 根据当前的 [售后类型, 退款状态，最后一条操作记录] 来匹配 hit array，来确定当前的阶段
 * 3. 售后详情 CardInfo 根据 cardInfo 里的内容来展示具体的文案
 */
export const AFTERSALE_STAGE_ENTITIES: {
    [key: string]: {
        [key: string]: {
            stageName: typeof AFTERSALE_STAGE_TYPES[keyof typeof AFTERSALE_STAGE_TYPES];
            hit: (
                | AFTERSALE_STATUS
                | ORDER_REFUND_STATUS
                | ORDER_REFUND_STATUS[]
                | typeof AFTERSALE_OP_TYPE[keyof typeof AFTERSALE_OP_TYPE]
            )[];
            stageStyle: {
                hiddenStages: boolean;
                success: boolean | null;
            };
            cardInfo: (detail: Detail, lastOpRecord: AfterSaleRecord | undefined) => CardInfo;
        };
    };
} = {
    [AFTERSALE_TYPE.Init]: {
        [AFTERSALE_ALL_STATUS.售后退货退款_申请售后]: {
            stageName: AFTERSALE_STAGE_TYPES.apply,
            hit: [AFTERSALE_STATUS.Apply, ORDER_REFUND_STATUS.Waiting, AFTERSALE_OP_TYPE.apply],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                const { node_final_time, seller_word } = detail;
                const deadline = aftersaleHumanizeDate(node_final_time);
                return {
                    title: '退货申请中',
                    description:
                        detail.need_payee && deadline ? (
                            <>
                                <p
                                    className={`aftersale-card__footer__text${
                                        detail.seller_word ? ' hair-line bottom' : ''
                                    }`}
                                >
                                    商家将在<span>{deadline}</span>
                                    天内处理，超时自动同意，请您及时填写收款信息避免打款失败
                                </p>
                                {seller_word && (
                                    <p className="aftersale-card__footer__desc">
                                        商家备注：{seller_word}
                                    </p>
                                )}
                            </>
                        ) : (
                            deadline && (
                                <>
                                    <p
                                        className={`aftersale-card__footer__text${
                                            detail.seller_word ? ' hair-line bottom' : ''
                                        }`}
                                    >
                                        商家将在
                                        <span>{deadline}</span>
                                        内处理，超时自动同意
                                    </p>
                                    {seller_word && (
                                        <p className="aftersale-card__footer__desc">
                                            商家备注：{seller_word}
                                        </p>
                                    )}
                                </>
                            )
                        )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_打款失败]: {
            stageName: AFTERSALE_STAGE_TYPES.refund,
            hit: [
                AFTERSALE_STATUS.Success,
                ORDER_REFUND_STATUS.Fail,
                AFTERSALE_OP_TYPE.refund_fail
            ],
            stageStyle: {
                hiddenStages: false,
                success: false
            },
            cardInfo: (detail) => {
                return {
                    title: '退款失败',
                    description: (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                请修改收款信息，填写完成后自动退款
                                {detail.fail_reason && (
                                    <i className="aftersale-card__footer__light">
                                        <br /> 失败原因：{detail.fail_reason}
                                    </i>
                                )}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_取消申请]: {
            stageName: AFTERSALE_STAGE_TYPES.apply,
            hit: [
                AFTERSALE_STATUS.Close,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.cancel_apply
            ],
            stageStyle: {
                hiddenStages: true,
                success: true
            },
            cardInfo: (detail) => {
                const deadline = aftersaleHumanizeDate(detail.node_final_time);

                return {
                    title: '退货已取消',
                    description: deadline && (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                <span>{deadline}</span>后退款关闭
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_商户审核_同意]: {
            stageName: AFTERSALE_STAGE_TYPES.audit,
            hit: [AFTERSALE_STATUS.Return, ORDER_REFUND_STATUS.Waiting, AFTERSALE_OP_TYPE.audit],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                const deadline = aftersaleHumanizeDate(detail.node_final_time);

                return {
                    title: '待退货',
                    description: deadline && (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                商家已同意退货，请将商品退回商家指定退货地址，并在
                                <span>{deadline}</span>
                                内填写退货物流信息
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_商户审核_拒绝]: {
            stageName: AFTERSALE_STAGE_TYPES.audit,
            hit: [AFTERSALE_STATUS.Refuse, ORDER_REFUND_STATUS.Waiting, AFTERSALE_OP_TYPE.audit],
            stageStyle: {
                hiddenStages: false,
                success: false
            },
            cardInfo: (detail, lastOpRecord) => {
                const deadline = aftersaleHumanizeDate(detail.node_final_time);

                return {
                    title: '商家已拒绝',
                    description: deadline && (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                商家已拒绝退货，对结果不满意您可以在
                                <span>{deadline}</span>
                                内申请客服申诉
                                {lastOpRecord && lastOpRecord.comment && (
                                    <i className="aftersale-card__footer__light">
                                        <br /> 拒绝原因：{lastOpRecord.comment}
                                    </i>
                                )}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_用户申请仲裁]: {
            stageName: AFTERSALE_STAGE_TYPES.audit,
            hit: [
                AFTERSALE_STATUS.Refuse,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.apply_arbitrate
            ],
            stageStyle: {
                hiddenStages: false,
                success: false
            },
            cardInfo: (detail) => {
                return {
                    title: '客服处理中',
                    description: (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                您已申请官方客服介入，请保证电话畅通
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_客服仲裁_支持用户]: {
            stageName: AFTERSALE_STAGE_TYPES.audit,
            hit: [
                AFTERSALE_STATUS.Return,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.arbitrate_audit
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                const deadline = aftersaleHumanizeDate(detail.node_final_time);

                return {
                    title: '待退货',
                    description: deadline && (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                客服已同意退货，请将商品退回商家指定退货地址，并在
                                <span>{deadline}</span>
                                内填写退货物流信息
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_客服仲裁_支持商家]: {
            stageName: AFTERSALE_STAGE_TYPES.audit,
            hit: [
                AFTERSALE_STATUS.Close,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.arbitrate_audit
            ],
            stageStyle: {
                hiddenStages: false,
                success: false
            },
            cardInfo: (detail, lastOpRecord) => {
                const deadline = aftersaleHumanizeDate(detail.node_final_time);

                return {
                    title: '客服已拒绝',
                    description: deadline && (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                客服已拒绝退款，退款将在
                                <span>{deadline}</span>内关闭
                                {lastOpRecord && lastOpRecord.comment && (
                                    <i className="aftersale-card__footer__light">
                                        <br /> 拒绝原因：{lastOpRecord.comment}
                                    </i>
                                )}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_填写物流]: {
            stageName: AFTERSALE_STAGE_TYPES.logistics,
            hit: [
                AFTERSALE_STATUS.Logistics,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.logistics
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                const { return_logistics } = detail;
                const isSellerExtend = !!detail.seller_extend_receive_time;
                if (isSellerExtend) {
                    return {
                        title: '等待商家处理',
                        description: (
                            <>
                                <p
                                    className={`aftersale-card__footer__text${
                                        detail.seller_word ? ' hair-line bottom' : ''
                                    }`}
                                >
                                    商家未收到您的退货商品，已申请延长收货时间，商家将在签收订单后2天内处理，如果未收到货本次延长7天后自动退款
                                </p>
                                {detail.seller_word && (
                                    <p className="aftersale-card__footer__desc">
                                        商家备注：{detail.seller_word}
                                    </p>
                                )}
                            </>
                        )
                    };
                }
                const deadline = aftersaleHumanizeDate(detail.node_final_time);
                const isSign = return_logistics.state === LOGISTICS_STATE.isSign;
                if (isSign) {
                    return {
                        title: '已退货',
                        description: deadline && (
                            <>
                                <p
                                    className={`aftersale-card__footer__text${
                                        detail.seller_word ? ' hair-line bottom' : ''
                                    }`}
                                >
                                    商家已收到您的退货商品，将在
                                    <span>{deadline}</span>
                                    内处理，超时自动同意
                                </p>
                            </>
                        )
                    };
                }
                return {
                    title: '已退货',
                    description: deadline && (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                您已填写退货信息，商家将在签收订单后<span>48</span>
                                小时内处理，发货后<span>7</span>天未签收自动同意退款
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_货物审核_同意]: {
            stageName: AFTERSALE_STAGE_TYPES.logistics,
            hit: [
                AFTERSALE_STATUS.Success,
                [ORDER_REFUND_STATUS.Waiting, ORDER_REFUND_STATUS.Refunding],
                AFTERSALE_OP_TYPE.logistics_audit
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                const deadline = aftersaleHumanizeDate(detail.node_final_time);

                return {
                    title: '商家已同意',
                    description: (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                {detail.need_payee
                                    ? `商家已同意退款，请填写收款信息`
                                    : `商家已同意退款，退款将返回您的${
                                          PAY_RETURN_PATH[detail.pay_type]
                                      }账户`}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_货物审核_同意_退款失败]: {
            stageName: AFTERSALE_STAGE_TYPES.logistics,
            hit: [
                AFTERSALE_STATUS.Success,
                [ORDER_REFUND_STATUS.Waiting, ORDER_REFUND_STATUS.Refunding],
                AFTERSALE_OP_TYPE.refund_fail
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                const deadline = aftersaleHumanizeDate(detail.node_final_time);
                return {
                    title: '商家已同意',
                    description: (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                {detail.need_payee
                                    ? `商家已同意退款，请填写收款信息`
                                    : `商家已同意退款，退款将返回您的${
                                          PAY_RETURN_PATH[detail.pay_type]
                                      }账户`}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_货物审核_拒绝]: {
            stageName: AFTERSALE_STAGE_TYPES.refund,
            hit: [
                AFTERSALE_STATUS.LogisticsRefuse,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.logistics_audit
            ],
            stageStyle: {
                hiddenStages: false,
                success: false
            },
            cardInfo: (detail, lastOpRecord) => {
                const deadline = aftersaleHumanizeDate(detail.node_final_time);

                return {
                    title: '商家已拒绝',
                    description: deadline && (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                商家已拒绝退款，退款将在
                                <span>{deadline}</span>
                                内关闭
                                {lastOpRecord && lastOpRecord.comment && (
                                    <i className="aftersale-card__footer__light">
                                        <br /> 拒绝原因：{lastOpRecord.comment}
                                    </i>
                                )}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_退货仲裁]: {
            stageName: AFTERSALE_STAGE_TYPES.logistics,
            hit: [
                AFTERSALE_STATUS.LogisticsRefuse,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.apply_logistics_arbitrate
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                return {
                    title: '客服处理中',
                    description: (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                您已申请官方客服介入，请保证电话畅通
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_退货仲裁_支持用户]: {
            stageName: AFTERSALE_STAGE_TYPES.logistics,
            hit: [
                AFTERSALE_STATUS.Success,
                [ORDER_REFUND_STATUS.Waiting, ORDER_REFUND_STATUS.Refunding],
                AFTERSALE_OP_TYPE.logistics_arbitrate_audit
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                return {
                    title: '客服已同意',
                    description: (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                {detail.need_payee
                                    ? '客服已同意退款，请填写收款信息'
                                    : `客服已同意退款，退款将返回您的${
                                          PAY_RETURN_PATH[detail.pay_type]
                                      }账户`}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_退货仲裁_支持商家]: {
            stageName: AFTERSALE_STAGE_TYPES.refund,
            hit: [
                AFTERSALE_STATUS.Close,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.logistics_arbitrate_audit
            ],
            stageStyle: {
                hiddenStages: false,
                success: false
            },
            cardInfo: (detail, lastOpRecord) => {
                const deadline = aftersaleHumanizeDate(detail.node_final_time);

                return {
                    title: '客服已拒绝',
                    description: deadline && (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                客服已拒绝退款，退款将在
                                <span>{deadline}</span>
                                内关闭
                                {lastOpRecord && lastOpRecord.comment && (
                                    <i className="aftersale-card__footer__light">
                                        <br /> 拒绝原因：{lastOpRecord.comment}
                                    </i>
                                )}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_退款成功]: {
            stageName: AFTERSALE_STAGE_TYPES.refund,
            hit: [AFTERSALE_STATUS.Success, ORDER_REFUND_STATUS.Success, AFTERSALE_OP_TYPE.refund],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                return {
                    title: '退款成功',
                    description: (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                {`退款已返回您的${PAY_RETURN_PATH[detail.pay_type]}账户`}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_退款失败]: {
            stageName: AFTERSALE_STAGE_TYPES.refund,
            hit: [AFTERSALE_STATUS.Success, ORDER_REFUND_STATUS.Fail, AFTERSALE_OP_TYPE.refund],
            stageStyle: {
                hiddenStages: true,
                success: true
            },
            cardInfo: (detail) => {
                return {
                    title: '退款失败',
                    description: (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                退款失败，原因<span>{detail.reason}</span>，请联系官方客服处理
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        }
    },
    [AFTERSALE_TYPE.Refund]: {
        [AFTERSALE_ALL_STATUS.售后退款_申请售后]: {
            stageName: AFTERSALE_STAGE_TYPES.apply,
            hit: [AFTERSALE_STATUS.Apply, ORDER_REFUND_STATUS.Waiting, AFTERSALE_OP_TYPE.apply],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                const deadline = aftersaleHumanizeDate(detail.node_final_time);

                return {
                    title: '退款申请中',
                    description:
                        deadline &&
                        (detail.need_payee ? (
                            <>
                                <p
                                    className={`aftersale-card__footer__text${
                                        detail.seller_word ? ' hair-line bottom' : ''
                                    }`}
                                >
                                    商家将在
                                    <span>{deadline}</span>
                                    内处理，超时自动同意，请您及时填写收款信息避免打款失败
                                </p>
                                {detail.seller_word && (
                                    <p className="aftersale-card__footer__desc">
                                        商家备注：{detail.seller_word}
                                    </p>
                                )}
                            </>
                        ) : (
                            <>
                                <p
                                    className={`aftersale-card__footer__text${
                                        detail.seller_word ? ' hair-line bottom' : ''
                                    }`}
                                >
                                    商家将在
                                    <span>{deadline}</span>
                                    内处理，超时自动退款
                                </p>
                                {detail.seller_word && (
                                    <p className="aftersale-card__footer__desc">
                                        商家备注：{detail.seller_word}
                                    </p>
                                )}
                            </>
                        ))
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退款_取消申请]: {
            stageName: AFTERSALE_STAGE_TYPES.apply,
            hit: [
                AFTERSALE_STATUS.Close,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.cancel_apply
            ],
            stageStyle: {
                hiddenStages: true,
                success: true
            },
            cardInfo: (detail) => {
                const deadline = aftersaleHumanizeDate(detail.node_final_time);

                return {
                    title: '退款已取消',
                    description: deadline && (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                <span>{deadline}</span>后退款关闭
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退款_商户审核_同意]: {
            stageName: AFTERSALE_STAGE_TYPES.audit,
            hit: [
                AFTERSALE_STATUS.Success,
                [ORDER_REFUND_STATUS.Waiting, ORDER_REFUND_STATUS.Refunding],
                AFTERSALE_OP_TYPE.audit
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                return {
                    title: '商家已同意',
                    description: (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                {detail.need_payee
                                    ? '商家已同意退款，请填写收款信息'
                                    : `商家已同意退款，退款将返回您的${
                                          PAY_RETURN_PATH[detail.pay_type]
                                      }账户`}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退款_商户审核_拒绝]: {
            stageName: AFTERSALE_STAGE_TYPES.audit,
            hit: [AFTERSALE_STATUS.Refuse, ORDER_REFUND_STATUS.Waiting, AFTERSALE_OP_TYPE.audit],
            stageStyle: {
                hiddenStages: false,
                success: false
            },
            cardInfo: (detail, lastOpRecord) => {
                const deadline = aftersaleHumanizeDate(detail.node_final_time);

                return {
                    title: '商家已拒绝',
                    description: deadline && (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                商家已拒绝退款，对结果不满意您可以在
                                <span>{deadline}</span>
                                内申请客服申诉
                                {lastOpRecord && lastOpRecord.comment && (
                                    <i className="aftersale-card__footer__light">
                                        <br /> 拒绝原因：{lastOpRecord.comment}
                                    </i>
                                )}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退款_用户申请仲裁]: {
            stageName: AFTERSALE_STAGE_TYPES.audit,
            hit: [
                AFTERSALE_STATUS.Refuse,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.apply_arbitrate
            ],
            stageStyle: {
                hiddenStages: false,
                success: false
            },
            cardInfo: (detail) => {
                return {
                    title: '客服处理中',
                    description: (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                您已申请官方客服介入，请保证电话畅通
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退款_客服仲裁_支持用户]: {
            stageName: AFTERSALE_STAGE_TYPES.audit,
            hit: [
                AFTERSALE_STATUS.Success,
                [ORDER_REFUND_STATUS.Waiting, ORDER_REFUND_STATUS.Refunding],
                AFTERSALE_OP_TYPE.arbitrate_audit
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                return {
                    title: '客服已同意',
                    description: (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                {detail.need_payee
                                    ? '客服已同意退款，请填写收款信息'
                                    : `客服已同意退款，退款将返回您的${
                                          PAY_RETURN_PATH[detail.pay_type]
                                      }账户`}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退款_客服仲裁_支持商家]: {
            stageName: AFTERSALE_STAGE_TYPES.audit,
            hit: [
                AFTERSALE_STATUS.Close,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.arbitrate_audit
            ],
            stageStyle: {
                hiddenStages: false,
                success: false
            },
            cardInfo: (detail, lastOpRecord) => {
                const deadline = aftersaleHumanizeDate(detail.node_final_time);

                return {
                    title: '客服已拒绝',
                    description: deadline && (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                客服已拒绝退款，退款将在
                                <span>{deadline}</span>内关闭
                                {lastOpRecord && lastOpRecord.comment && (
                                    <i className="aftersale-card__footer__light">
                                        <br /> 拒绝原因：{lastOpRecord.comment}
                                    </i>
                                )}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退款_客退款成功]: {
            stageName: AFTERSALE_STAGE_TYPES.refund,
            hit: [AFTERSALE_STATUS.Success, ORDER_REFUND_STATUS.Success, AFTERSALE_OP_TYPE.refund],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                return {
                    title: '退款成功',
                    description: (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                {`退款已返回您的${PAY_RETURN_PATH[detail.pay_type]}账户`}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_商家审核_退款失败]: {
            stageName: AFTERSALE_STAGE_TYPES.refund,
            hit: [
                AFTERSALE_STATUS.Success,
                ORDER_REFUND_STATUS.Fail,
                AFTERSALE_OP_TYPE.refund_fail
            ],
            stageStyle: {
                hiddenStages: false,
                success: false
            },
            cardInfo: (detail) => {
                return {
                    title: '退款失败',
                    description: (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                请修改收款信息，填写完成后自动退款
                                {detail.fail_reason && (
                                    <i className="aftersale-card__footer__light">
                                        <br /> 失败原因：{detail.fail_reason}
                                    </i>
                                )}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后退货退款_商家审核_商家同意]: {
            stageName: AFTERSALE_STAGE_TYPES.audit,
            hit: [
                AFTERSALE_STATUS.Success,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.refund_fail
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                return {
                    title: '商家已同意',
                    description: (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                {detail.need_payee
                                    ? `商家已同意退款，请填写收款信息`
                                    : `商家已同意退款，退款将返回您的${
                                          PAY_RETURN_PATH[detail.pay_type]
                                      }账户`}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        }
    },
    [AFTERSALE_TYPE.PresaleAll]: {
        [AFTERSALE_ALL_STATUS.售前退款_申请售后]: {
            stageName: AFTERSALE_STAGE_TYPES.apply,
            hit: [AFTERSALE_STATUS.Apply, ORDER_REFUND_STATUS.Waiting, AFTERSALE_OP_TYPE.apply],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                const deadline = aftersaleHumanizeDate(detail.node_final_time);

                return {
                    title: '退款申请中',
                    description: deadline && (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                商家将在
                                <span>{deadline}</span>
                                内处理，超时自动退款
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售前退款_取消申请]: {
            stageName: AFTERSALE_STAGE_TYPES.apply,
            hit: [
                AFTERSALE_STATUS.Close,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.cancel_apply
            ],
            stageStyle: {
                hiddenStages: true,
                success: true
            },
            cardInfo: () => {
                return {
                    title: '退款已取消'
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售前退款_商户审核_同意]: {
            stageName: AFTERSALE_STAGE_TYPES.audit,
            hit: [
                AFTERSALE_STATUS.Success,
                [ORDER_REFUND_STATUS.Waiting, ORDER_REFUND_STATUS.Refunding],
                AFTERSALE_OP_TYPE.audit
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                return {
                    title: '退款已同意',
                    description: (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                {`商家已同意退款，退款将返回您的${
                                    PAY_RETURN_PATH[detail.pay_type]
                                }账户`}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售前退款_退款成功]: {
            stageName: AFTERSALE_STAGE_TYPES.refund,
            hit: [AFTERSALE_STATUS.Success, ORDER_REFUND_STATUS.Success, AFTERSALE_OP_TYPE.refund],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                return {
                    title: '退款成功',
                    description: (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                {`退款已返回您的${PAY_RETURN_PATH[detail.pay_type]}账户`}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        }
    },
    [AFTERSALE_TYPE.Exchange]: {
        [AFTERSALE_ALL_STATUS.售后换货_申请售后]: {
            stageName: AFTERSALE_STAGE_TYPES.apply,
            hit: [AFTERSALE_STATUS.Apply, ORDER_REFUND_STATUS.Waiting, AFTERSALE_OP_TYPE.apply],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                const deadline = aftersaleHumanizeDate(detail.aftersale_left_receive_time!);
                return {
                    title: '等待商家处理',
                    description: deadline && (
                        <>
                            <p className="aftersale-card__footer__text hair-line bottom">
                                商家将在 <span>{deadline}</span> 内处理您的换货申请，超时自动同意
                            </p>
                            <p className="aftersale-card__footer__desc">
                                若商家同意：换货申请达成，请您及时退货
                                <br />
                                若商家拒绝：换货申请将关闭，您可以联系商家协商处理
                            </p>
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后换货_商户审核_同意]: {
            stageName: AFTERSALE_STAGE_TYPES.audit,
            hit: [AFTERSALE_STATUS.Return, ORDER_REFUND_STATUS.Waiting, AFTERSALE_OP_TYPE.audit],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                const { aftersale_left_receive_time, exchange_logistics, seller_word } = detail;
                const logistics = {
                    returnAddressDesc: detail.return_address_desc,
                    returnMobile: detail.return_mobile,
                    returnUserName: detail.return_user_name
                };
                const deadline = aftersaleHumanizeDate(aftersale_left_receive_time!);

                return {
                    title: '请寄回商品并填写物流信息',
                    description: deadline && (
                        <>
                            <p className="aftersale-card__footer__text hair-line bottom">
                                商家已同意换货申请，请尽早将商品寄回商家指定地址，并在{' '}
                                <span>{deadline}</span> 内填写退货物流信息
                            </p>
                            <Address logistics={logistics} hasBottom={seller_word} />
                            {seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后换货_商户审核_拒绝]: {
            stageName: AFTERSALE_STAGE_TYPES.audit,
            hit: [AFTERSALE_STATUS.Refuse, ORDER_REFUND_STATUS.Waiting, AFTERSALE_OP_TYPE.audit],
            stageStyle: {
                hiddenStages: false,
                success: false
            },
            cardInfo: (detail, lastOpRecord) => {
                const deadline = aftersaleHumanizeDate(detail.aftersale_left_receive_time!);

                return {
                    title: '商家拒绝换货',
                    description: deadline && (
                        <>
                            <p className="aftersale-card__footer__text hair-line bottom">
                                对结果不满意请在 <span>{deadline}</span> 内申请客服介入
                                {lastOpRecord && lastOpRecord.comment && (
                                    <i className="aftersale-card__footer__light">
                                        <br /> 拒绝原因：{lastOpRecord.comment}
                                    </i>
                                )}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后换货_用户申请仲裁]: {
            stageName: AFTERSALE_STAGE_TYPES.audit,
            hit: [
                AFTERSALE_STATUS.Refuse,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.apply_arbitrate
            ],
            stageStyle: {
                hiddenStages: false,
                success: false
            },
            cardInfo: () => {
                return {
                    title: '客服处理中',
                    description: '您已申请官方客服介入，请耐心等待，保证电话畅通'
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后换货_客服仲裁_支持用户]: {
            stageName: AFTERSALE_STAGE_TYPES.audit,
            hit: [
                AFTERSALE_STATUS.Return,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.arbitrate_audit
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                const { aftersale_left_receive_time, exchange_logistics, seller_word } = detail;
                const deadline = aftersaleHumanizeDate(aftersale_left_receive_time!);
                const { exchange_post_receiver, exchange_post_tel } = exchange_logistics || {};
                let exchange_post_addr = '';
                try {
                    const cityList = JSON.parse(exchange_logistics.exchange_post_addr || '');
                    exchange_post_addr = `${['province', 'city', 'town']
                        .map((x) => cityList[x].name)
                        .join('')}${cityList.detail}`;
                } catch (err) {
                    exchange_post_addr = exchange_logistics.exchange_post_addr;
                }
                const logistics = {
                    returnAddressDesc: exchange_post_addr,
                    returnMobile: exchange_post_tel,
                    returnUserName: exchange_post_receiver
                };

                return {
                    title: '请寄回商品并填写物流信息',
                    description: deadline && (
                        <>
                            <p className="aftersale-card__footer__text hair-line bottom">
                                客服已同意换货，请将商品寄回商家指定退货地址，并在{' '}
                                <span>{deadline}</span> 内填写退货物流信息
                            </p>
                            <Address logistics={logistics} hasBottom={seller_word} />
                            {seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后换货_客服仲裁_支持商家]: {
            stageName: AFTERSALE_STAGE_TYPES.audit,
            hit: [
                AFTERSALE_STATUS.Close,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.arbitrate_audit
            ],
            stageStyle: {
                hiddenStages: false,
                success: false
            },
            cardInfo: (_, lastOpRecord) => {
                return {
                    title: '客服已拒绝换货',
                    description: '客服已拒绝您的换货申请',
                    reason:
                        lastOpRecord && lastOpRecord.comment && `拒绝原因：${lastOpRecord.comment}`
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后换货_已填写物流]: {
            stageName: AFTERSALE_STAGE_TYPES.backGoods,
            hit: [
                AFTERSALE_STATUS.Logistics,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.logistics
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                const { return_logistics } = detail;
                const isSellerExtend = !!detail.seller_extend_receive_time;
                if (isSellerExtend) {
                    return {
                        title: '等待商家处理',
                        description:
                            '商家未收到您的退货商品，已申请延长收货时间，商家将在签收订单后2天内发回换货商品，如果未收到货本次延长7天后自动退款'
                    };
                }
                const deadline = aftersaleHumanizeDate(detail.aftersale_left_receive_time!);
                const isSign = return_logistics.state === LOGISTICS_STATE.isSign;
                if (isSign) {
                    const deadlineTime = aftersaleHumanizeDate(detail.node_final_time);
                    return {
                        title: '等待商家处理',
                        description: deadline && (
                            <>
                                <p
                                    className={`aftersale-card__footer__text${
                                        detail.seller_word ? ' hair-line bottom' : ''
                                    }`}
                                >
                                    商家已收到您的退货商品，将在签收订单后
                                    <span>{deadlineTime}</span>
                                    内发回换货商品，超时未处理自动退款
                                </p>
                            </>
                        )
                    };
                }
                return {
                    title: '等待商家处理',
                    description: (
                        <div>
                            您已填写退货信息，商家将在签收订单后<span>48</span>
                            小时内发回换货商品，超时未处理自动退款
                        </div>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后换货_商家已发货]: {
            stageName: AFTERSALE_STAGE_TYPES.resendGoods,
            hit: [
                AFTERSALE_STATUS.HaveSend,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.logistics_exchange
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: () => ({
                title: '商家已发货',
                description: '商家已发货，请注意查看物流信息，7天后系统自动确认收货'
            })
        },
        [AFTERSALE_ALL_STATUS.售后换货_已填写物流_商户拒绝]: {
            stageName: AFTERSALE_STAGE_TYPES.resendGoods,
            hit: [
                AFTERSALE_STATUS.LogisticsRefuse,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.logistics_audit
            ],
            stageStyle: {
                hiddenStages: false,
                success: false
            },
            cardInfo: (detail, lastOpRecord) => {
                const deadline = aftersaleHumanizeDate(detail.aftersale_left_receive_time!);

                return {
                    title: '商家拒绝换货',
                    description: deadline && (
                        <>
                            <p
                                className={`aftersale-card__footer__text${
                                    detail.seller_word ? ' hair-line bottom' : ''
                                }`}
                            >
                                对结果不满意请在 <span>{deadline}</span> 内申请客服介入
                                {lastOpRecord && lastOpRecord.comment && (
                                    <i className="aftersale-card__footer__light">
                                        <br /> 拒绝原因：{lastOpRecord.comment}
                                    </i>
                                )}
                            </p>
                            {detail.seller_word && (
                                <p className="aftersale-card__footer__desc">
                                    商家备注：{detail.seller_word}
                                </p>
                            )}
                        </>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后换货_已填写物流_用户申请仲裁]: {
            stageName: AFTERSALE_STAGE_TYPES.backGoods,
            hit: [
                AFTERSALE_STATUS.LogisticsRefuse,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.apply_logistics_arbitrate
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: () => {
                return {
                    title: '客服处理中',
                    description: '您已申请官方客服介入，请耐心等待，保证电话畅通'
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后换货_已填写物流_客服支持用户]: {
            stageName: AFTERSALE_STAGE_TYPES.backGoods,
            hit: [
                AFTERSALE_STATUS.Logistics,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.logistics_arbitrate_audit
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                const isSellerExtend = !!detail.seller_extend_receive_time;
                if (isSellerExtend) {
                    return {
                        title: '等待商家处理',
                        description:
                            '商家未收到您的退货商品，已申请延长收货时间，商家将在签收订单后2天内发回换货商品，如果未收到货本次延长7天后自动退款'
                    };
                }
                const deadline = aftersaleHumanizeDate(detail.aftersale_left_receive_time!);

                return {
                    title: '客服同意换货',
                    description: deadline && (
                        <div>
                            客服已同意换货，商家将在 <span>{deadline}</span> 内发货
                        </div>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后换货_二次仲裁_客服支持用户]: {
            stageName: AFTERSALE_STAGE_TYPES.backGoods,
            hit: [
                AFTERSALE_STATUS.Logistics,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.exchange_arbitrate_audit
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: (detail) => {
                const isSellerExtend = !!detail.seller_extend_receive_time;
                if (isSellerExtend) {
                    return {
                        title: '等待商家处理',
                        description:
                            '商家未收到您的退货商品，已申请延长收货时间，商家将在签收订单后2天内发回换货商品，如果未收到货本次延长7天后自动退款'
                    };
                }
                const deadline = aftersaleHumanizeDate(detail.aftersale_left_receive_time!);

                return {
                    title: '客服同意换货',
                    description: deadline && (
                        <div>
                            客服已同意换货，商家将在 <span>{deadline}</span> 内发货
                        </div>
                    )
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后换货_客服同意退款]: {
            stageName: AFTERSALE_STAGE_TYPES.backGoods,
            hit: [AFTERSALE_STATUS.Success, ORDER_REFUND_STATUS.Waiting, AFTERSALE_OP_TYPE.refund],
            stageStyle: {
                hiddenStages: true,
                success: true
            },
            cardInfo: () => {
                return {
                    title: '退款完成',
                    description: '退款将返回至您的微信/支付宝账户'
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后换货_已填写物流_客服支持商家]: {
            stageName: AFTERSALE_STAGE_TYPES.resendGoods,
            hit: [
                AFTERSALE_STATUS.Close,
                ORDER_REFUND_STATUS.Waiting,
                AFTERSALE_OP_TYPE.logistics_arbitrate_audit
            ],
            stageStyle: {
                hiddenStages: false,
                success: false
            },
            cardInfo: (_, lastOpRecord) => {
                return {
                    title: '客服拒绝换货',
                    description: '客服已拒绝您的换货申请将48小时内将自动退款',
                    reason:
                        lastOpRecord && lastOpRecord.comment && `拒绝原因：${lastOpRecord.comment}`
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后换货_换货成功]: {
            stageName: AFTERSALE_STAGE_TYPES.exchange,
            hit: [
                AFTERSALE_STATUS.Close,
                ORDER_REFUND_STATUS.Success,
                AFTERSALE_OP_TYPE.cancel_apply
            ],
            stageStyle: {
                hiddenStages: true,
                success: true
            },
            cardInfo: () => ({
                title: '换货已取消'
            })
        },
        [AFTERSALE_ALL_STATUS.售后换货_换货取消]: {
            stageName: AFTERSALE_STAGE_TYPES.exchange,
            hit: [
                AFTERSALE_STATUS.Success,
                ORDER_REFUND_STATUS.Success,
                AFTERSALE_OP_TYPE.success_exchange
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: () => ({
                title: '换货成功',
                description: '如您未收到商品或商品质量有问题，可以联系商家重新申请售后'
            })
        },
        [AFTERSALE_ALL_STATUS.售后换货_换货_退款成功]: {
            stageName: AFTERSALE_STAGE_TYPES.resendGoods,
            hit: [
                AFTERSALE_STATUS.Success,
                [ORDER_REFUND_STATUS.Waiting, ORDER_REFUND_STATUS.Refunding],
                AFTERSALE_OP_TYPE.logistics_audit
            ],
            stageStyle: {
                hiddenStages: false,
                success: false
            },
            cardInfo: (detail) => {
                return {
                    title: ['同意退款', '退款执行中', '退款已完成', '退款已关闭'][
                        detail.refund_status || 0
                    ],
                    description: '退款将返回至您的微信/支付宝账户'
                };
            }
        },
        [AFTERSALE_ALL_STATUS.售后换货_用户接收]: {
            stageName: AFTERSALE_STAGE_TYPES.exchange,
            hit: [
                AFTERSALE_STATUS.UserReceive,
                ORDER_REFUND_STATUS.Success,
                AFTERSALE_OP_TYPE.success_exchange
            ],
            stageStyle: {
                hiddenStages: false,
                success: true
            },
            cardInfo: () => ({
                title: '换货成功',
                description: '如您未收到商品或商品质量有问题，可以联系商家重新申请售后'
            })
        },
        [AFTERSALE_ALL_STATUS.售后换货_换货失败]: {
            stageName: AFTERSALE_STAGE_TYPES.exchange,
            hit: [
                AFTERSALE_STATUS.Close,
                ORDER_REFUND_STATUS.Fail,
                AFTERSALE_OP_TYPE.aftersale_fail
            ],
            stageStyle: {
                hiddenStages: true,
                success: true
            },
            cardInfo: (detail) => {
                return {
                    title: '换货已关闭',
                    description: detail.reason && (
                        <div>
                            关闭原因：<span>{detail.reason}</span>
                        </div>
                    )
                };
            }
        }
    }
};
